import markdownConfig from './markdown-config';

// HTTP URL regex to validate if a string is a full URL HHTTP or HTTPS
const HTTP_URL_REGEX =
  /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/i;
const INLINE_URL_REGEX =
  /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/gi;

// INFO: IDs for get the Labels translated in the app, used for links with URL as text
const LABEL_LINK = {
  MORE_INFO_TITLE: 'AMAIA_LABEL_LINK_MOREINFO',
  MORE_INFO_NOT_TITLE: 'AMAIA_LABEL_LINK_MOREINFO_NOT_TITLE',
};

/**
 * Check if the given text is a full URL 
 * @param {string} text - Text to check
 * @returns {boolean}
 */
export const isFullUrl = (text) => {
  if (!text || typeof text !== 'string') return false;
  return HTTP_URL_REGEX.test(text.trim());
};

/**
 * Convert Urls in plain text to markdown format [text](url)
 * Avoid converting URLs that are already in markdown format [text](url)
 * @param {string} text - text to process
 * @returns {string} - Text with URLs in markdown format
 */
export const linkifyPlainUrls = (text) => {
  if (!text) return '';

  const result = text.replace(INLINE_URL_REGEX, (match, offset) => {
    const prev2 = text.slice(offset - 2, offset);
    const prev1 = text.charAt(offset - 1);

    if (prev2 === '](' || prev1 === '<') {
      return match;
    }

    const markdownLink = `[${match}](${match})`;
    return markdownLink;
  });
  return result;
};

/**
 * Convert markdown links with URL as text
 * Limits CTA conversion to maximum 2 links per block (--- separator)
 * @param {string} text - Markdown text with links
 * @returns {string} - Text with CTA formatted links
 */
export const convertUrlsToCTA = (text, intl) => {
  if (!text) return '';

  const markdownLinkRegex = /\[(https?:\/\/[^\]]+)\]\((https?:\/\/[^)]+)\)/gi;
  const markdownListUrlLineRegex = /^\s*-\s+\[(https?:\/\/[^\]]+)\]\((https?:\/\/[^)]+)\)\s*$/i;
  const MAX_LINKS = 2;
  const BLOCK_SEPARATOR = /\n\s*---\s*\n/g;

  // INFO: Set to true to show copyId text instead of URL for links with URL as text
  const SHOW_COPY_ID = false; 

  const blocks = text.split(BLOCK_SEPARATOR);
  const convertedBlocks = blocks.map((block) => {
    let linkCount = 0;
    const limitedLines = block
      .split('\n')
      .filter((line) => {
        if (!markdownListUrlLineRegex.test(line)) {
          return true;
        }

        linkCount++;
        return linkCount <= MAX_LINKS;
      });

    return limitedLines
      .map((line) => line.replace(markdownLinkRegex, (match, linkText, linkUrl) => {
        if (linkText === linkUrl) {
          const ctaText = getLinkText(SHOW_COPY_ID, linkUrl, intl);
          return `[${ctaText}](${linkUrl})`;
        }

        return match;
      }))
      .join('\n');
  });

  return convertedBlocks.join('\n---\n');
};

/**
 * render markdown text to HTML and sanitize it to prevent XSS attacks
 * @param {string} text - Markdown text to render
 * 
 * @returns {string} - Sanitized HTML
 */
export const sanitizeAndRenderMarkdown = (text, intl) => {
  if (!text) return '';

  const md = markdownConfig.getMarkdownRenderer();
  const linkedText = linkifyPlainUrls(text);
  const ctaText = convertUrlsToCTA(linkedText, intl);
  const renderedHtml = md.render(ctaText);
  const sanitizedHtml = markdownConfig.sanitizeHtml(renderedHtml);

  return sanitizedHtml;
};

/**
 * Get the text to display for a link based on the showCopyId flag and the link URL
 * @param {boolean} showCopyId 
 * @param {string} linkUrl 
 * @param {Object} intl
 * @returns {string} - The text to display for the link
 */
const getLinkText = (showCopyId, linkUrl, intl) => {
  // INFO: this implementation assumes that if the link text is the same as the URL,
  //  we want to show a copyId instead of the URL for better user experience, 
  // otherwise we keep the original link text (which could be a more descriptive text)
  if (showCopyId) {
    if (intl && typeof intl.formatMessage === 'function') {
      return intl.formatMessage({ id: LABEL_LINK.MORE_INFO_NOT_TITLE });
    }
    return LABEL_LINK.MORE_INFO_NOT_TITLE;
  }
  return linkUrl;  
};